import { BaseEntity } from './common';

// Customer types
export interface CustomerDTO extends BaseEntity {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  isActive: boolean;
  lastPurchaseDate?: string;
  totalPurchases: number;
  totalAmount: number;
}

export interface CreateCustomerDTO {
  name: string;
  phone: string;
  email?: string;
  address?: string;
}

// Bill item types
export interface BillItemDTO {
  uuid: string;
  billId: string;
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountAmount: number;
  tax: number;
  taxAmount: number;
  subtotal: number;
  total: number;
}

export interface CartItemDTO {
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  originalPrice?: number; // Product master price — never mutated, used to detect overrides
  discount: number;
  tax: number;
  availableStock?: number;
  description?: string;
  categoryName?: string;
  supplierName?: string;
  scale_unit?: string;
}

// Bill types
export enum PaymentType {
  CASH = 'cash',
  DEBIT_CARD = 'debit_card',
  CREDIT_CARD = 'credit_card',
  Banking = 'Banking',
  WALLET = 'wallet',
  UPI = 'upi',
  CREDIT = 'credit', // Credit sale
}

export enum BillStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  HELD = 'held',
}

export interface BillDTO extends BaseEntity {
  billNumber: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  cashierId: string;
  cashierName: string;
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  deliveryFees?: number;
  installationFees?: number;
  totalAmount: number;
  paidAmount: number;
  changeAmount: number;
  paymentType: PaymentType;
  status: BillStatus;
  notes?: string;
  completedAt?: string;
  items?: BillItemDTO[];
}

export interface CreateBillDTO {
  storeId?: string; // Store ID for the bill
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  items: {
    productId: string;
    productCode: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    discount: number;
    tax: number;
  }[];
  paidAmount: number;
  deliveryFees?: number;
  installationFees?: number;
  paymentType: PaymentType;
  notes?: string;
  billDate?: string; // Optional: date for the bill (defaults to current date on backend)
  isCreditSale?: boolean;
  creditDueDate?: string;
  bypassCreditLimit?: boolean;
}

// Search and query types
export interface CustomerSearchDTO {
  search?: string;
  phone?: string;
  page?: number;
  limit?: number;
}

export interface BillSearchDTO {
  search?: string;
  status?: string;
  paymentType?: string;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  storeId?: string;
}

// Product search result
export interface ProductSearchResult {
  status: boolean;
  message: string;
  data?: {
    uuid: string;
    item_code: string;
    item_name: string;
    description?: string;
    category: string;
    categoryName?: string;
    supplier: string;
    cost_price?: number;
    selling_price: number;
    tax: number;
    quantity_in_stock: number;
    storeId: string;
    status: string;
    product_image?: string;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

// Daily summary types
export interface DailySummaryDTO {
  totalBills: number;
  totalRevenue: number;
  totalTax: number;
  totalDiscount: number;
  avgBillAmount: number;
  cashPayments: number;
  cardPayments: number;
  digitalPayments: number;
}

// Pagination response types
export interface CustomerPaginationResponse {
  data: CustomerDTO[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface BillPaginationResponse {
  data: BillDTO[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Cart calculation types
export interface CartTotals {
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalAmount: number;
  totalQuantity: number;
}

// Bill form state
export interface BillFormState {
  cartItems: CartItemDTO[];
  selectedCustomer?: CustomerDTO;
  paymentType: PaymentType;
  paidAmount: number;
  notes: string;
  totals: CartTotals;
}
